/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {debounceTime, takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";

@Directive({
  selector: '[wellDebounceClick]',
  standalone: true
})
export class DebounceClickDirective implements OnInit, OnDestroy {

  @Input() debounceTime = 3000;

  private isDisabled = false;
  private clicks = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(private el: ElementRef, private renderer: Renderer2) {
  }

  ngOnInit() {
    this.el.nativeElement.addEventListener('click', this.captureClick, true);

    this.clicks
      .pipe(debounceTime(this.debounceTime), takeUntil(this.destroy$))
      .subscribe(() => {
        this.isDisabled = false;
        this.setDisabledState(false);
      });
  }

  ngOnDestroy() {
    this.el.nativeElement.removeEventListener('click', this.captureClick, true);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private captureClick = (event: MouseEvent) => {
    if (this.isDisabled) {
      event.stopImmediatePropagation();
      event.preventDefault();
    } else {
      this.isDisabled = true;
      this.clicks.next();
      this.setDisabledState(true);
    }
  };

  private setDisabledState(disabled: boolean) {
    const el = this.el.nativeElement;
    // For native <button> elements
    const button = el.tagName === 'BUTTON' ? el : el.querySelector('button');
    if (button) {
      this.renderer.setProperty(button, 'disabled', disabled);
    }
    // For PrimeNG p-button host element
    if (disabled) {
      this.renderer.setAttribute(el, 'disabled', 'true');
    } else {
      this.renderer.removeAttribute(el, 'disabled');
    }
  }
}
